import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { CommonService } from "app/profile/services/common.service";
import { I18nService } from 'app/shared/services/i18n.service';
import { MatDialog } from "@angular/material/dialog";
import { SoftwareState } from "app/profile/store/state/software.state";
import { Store } from "@ngrx/store";
import { Observable, BehaviorSubject } from "rxjs";
import {
    Software,
    Type,
    Category,
    SoftwareFilter
} from "app/profile/models/software.model";
import {
    getSoftwareCategories,
    getSoftwareTypes
} from "app/profile/store/selectors/software.selector";
import { SearchResult, GeneralSettings } from "app/profile/models/generic.model";
import { SoftwareService } from "app/profile/services/software.service";
import { PageEvent, TslDialog, TslSelectChange, TslSort } from "@tesla/ng-tds";
import { SoftwareFormDialogComponent } from "./software-form-dialog/software-form-dialog.component";
import { GetSoftwareCategories, GetSoftwareTypes } from 'app/profile/store/app.actions';
import { StateService } from 'app/profile/services/state.service';
import { Status, StatusEnum } from 'app/profile/models/state.model';
import { TabComponent } from '../tab-component';
import { getGeneralSettings } from 'app/profile/store/app.selector';
import { ProfileService } from 'app/profile/services/profile.service';
@Component({
    selector: "app-software",
    templateUrl: "./software.component.html",
    styleUrls: ["./software.component.scss"]
})
export class SoftwareComponent implements OnInit, TabComponent {
    data: any;
    supplierCode: string;
    deleteTitle: string;
    deleteConfirmation: string;
    softwareTypes$: Observable<Type[]>;
    softwareCategories$: Observable<Category[]>;
    softwareResult: SearchResult<Software> = new SearchResult<Software>();

    preventativeHold: boolean =false;

    // table
    datasource: BehaviorSubject<Array<Software>> = new BehaviorSubject([]);
    displayedColumns: Array<string> = ["softwareCategory", "softwareType", "version", "description", "action"];
    pageIndex = 0;
    pageSize = 10;
    pageSizeOptions: number[] = [10, 25, 50, 100, 250];

    // filter
    filter: SoftwareFilter = new SoftwareFilter();

    helpUrl: string;
    settings: GeneralSettings;

    isDirty(): boolean {
        return false;
    }

    constructor(
        private route: ActivatedRoute,
        private commonService: CommonService,
        private store: Store<SoftwareState>,
        public translate: I18nService,
        private stateService: StateService,
        private dialog: TslDialog,
        private matDialog: MatDialog,
        private service: SoftwareService,
        private profileService: ProfileService,
    ) { }

    ngOnInit() {
        this.store.select(getGeneralSettings).subscribe(res => {
            this.settings = res;
            if (res) {
                this.helpUrl = this.settings.settings.softwareHelpUrl;
            }
        });
        this.route.parent.params.subscribe(params => {
            if (!params["code"]) {
                return;
            }

            this.supplierCode = this.commonService.getSupplierCode(params["code"]);
            if (this.supplierCode !== null) {
                this.loadResources();
                this.profileService.getPreventativeStatus(this.supplierCode).subscribe(response => {
                    if (response) {
                        this.preventativeHold = response;
                    }
                });
            }
        });
    }

    onFilter(): void {
        let items = this.softwareResult.items;

        if (this.filter.softwareCategoryId > 0) {
            items = items.filter(x => x.softwareCategoryId === this.filter.softwareCategoryId);
        }

        if (this.filter.softwareTypeId > 0) {
            items = items.filter(x => x.softwareTypeId === this.filter.softwareTypeId);
        }

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(items.slice(skip, take));
    }

    onClearFilter(): void {
        this.filter = new SoftwareFilter();
        this.pageIndex = 0;
        this.pageSize = this.pageSizeOptions[0];
        this.onFilter();
    }

    onAdd(): void {
        const model = new Software();
        this.openSoftwareDialog(model);
    }

    onEdit(model: Software): void {
        this.openSoftwareDialog(model);
    }

    onDelete(model: Software): void {
        this.dialog.confirm(this.translate.get("Delete Confirmation"), this.translate.get("Are you sure you want to delete this item?"))
            .afterClosed()
            .subscribe(confirmed => {
                if (confirmed) {
                    this.service.delete(this.supplierCode, model.id).subscribe(_ => {
                        this.loadTable();
                    });
                }
            });
    }

    onPageChange(pageChange: PageEvent): void {
        const { pageIndex, pageSize } = pageChange;
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        this.onFilter();
    }

    onSortChange(sort: TslSort): void {
        const orderedItems = this.softwareResult.items.sort((a, b) => {
            const isAsc = sort.direction === 'asc';

            switch (sort.active) {
                case 'softwareCategory': return compare(a.softwareCategory.toLowerCase(), b.softwareCategory.toLowerCase(), isAsc);
                case 'softwareType': return compare(a.softwareType.toLowerCase(), b.softwareType.toLowerCase(), isAsc);
                case 'version': return compare(a.version.toLowerCase(), b.version.toLowerCase(), isAsc);
                case 'description': return compare(a.formats.toLowerCase(), b.formats.toLowerCase(), isAsc);
                default: return 0;
            }

        });

        const skip = this.pageIndex * this.pageSize;
        const take = (this.pageIndex + 1) * this.pageSize;
        this.datasource.next(orderedItems.slice(skip, take));

        function compare(a: number | string, b: number | string, isAsc: boolean) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
    }

    onChangeCategory(event: TslSelectChange): void {
        this.filter.softwareCategoryId = event.value;
    }

    private openSoftwareDialog(model: Software): void {
        const dialogRef = this.matDialog.open(SoftwareFormDialogComponent, {
            width: "600px",
            height: "450px",
            data: model
        });

        dialogRef.afterClosed()
            .subscribe(res => {
                if (res === undefined) {
                    return;
                }

                if (res.id === undefined || res.id === 0) {
                    this.service.create(this.supplierCode, res)
                        .subscribe(_ => {
                            if (res.softwareTypeId === 0) {
                                this.store.dispatch(new GetSoftwareTypes(this.supplierCode, true));
                            }

                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                            this.loadTable();
                        });
                } else {
                    this.service.update(this.supplierCode, res.id, res)
                        .subscribe(_ => {
                            if (res.softwareTypeId === 0) {
                                this.store.dispatch(new GetSoftwareTypes(this.supplierCode, true));
                            }

                            this.stateService.currentStatus.next(new Status(StatusEnum.Success));
                            this.loadTable();
                        });
                }
            });
    }

    private loadTable(): void {
        const filter = new SoftwareFilter();
        filter.skip = 0;
        filter.take = 10000;
        this.service.searchByFilters(this.supplierCode, filter)
            .subscribe(res => {
                this.softwareResult = res;
                this.onFilter();
            });
    }

    private loadResources(): void {
        this.store.dispatch(new GetSoftwareCategories());
        this.store.dispatch(new GetSoftwareTypes(this.supplierCode));

        this.softwareCategories$ = this.store.select(getSoftwareCategories);
        this.softwareTypes$ = this.store.select(getSoftwareTypes);

        //this.translate.get("Delete Confirmation").subscribe(t => (this.deleteTitle = t));
        //this.translate.get("Are you sure you want to delete this item?").subscribe(t => (this.deleteConfirmation = t));
        this.loadTable();
    }
}
